import React, { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  DialogModal,
  Drawer,
  Grid,
  Loader,
  NumberController,
  Panel,
  PanelHeader,
  RichController,
  SwitchController,
  Text,
  TextAreaController,
  TextController,
} from "@pushpress/shared-components";
import { AppointmentPackageForm } from "./AppointmentPackageDrawer.types";
import { useAppointmentPackageQuery } from "../../../apis/appointmentApi.hooks";
import {
  patchAppointmentPackage,
  postAppointmentPackage,
} from "../../../apis/appointmentApi";
import { AppContext } from "../../../context/app";
import { useTaxesQuery } from "../../../apis/clientApi.hooks";
import { extractError } from "../../../utils/misc";
import { getCurrencySymbol } from "../../../utils/currencyUtils";
import { useToggle } from "../../../hooks/state";
import AppointmentTypeSelect from "../../../components/controllers/AppointmentTypeSelect";
import AppointmentDiscountSelect from "../../../components/controllers/AppointmentDiscountSelect";
import TaxSelect from "../../../components/controllers/Deprecated/TaxSelect";
import { defaultToastOptions } from "../AppointmentsUtils";
import {
  appointmentPackageFormDefault,
  convertToAppointmentPackageForm,
  convertToAppointmentPostPackagePayload,
  convertToAppointmentPatchPackagePayload,
} from "./AppointmentPackageDrawer.utils";
import styles from "./AppointmentPackageDrawer.module.scss";

interface AppointmentPackageDrawerProps {
  visible: boolean;
  duplicate?: boolean;
  appointmentPackageUuid?: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function AppointmentPackageDrawer(props: AppointmentPackageDrawerProps) {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="420px"
      overflow="visible"
      disableClickAway
      disableEscapeKeyDown
    >
      <AppointmentPackageDrawerContent {...props} />
    </Drawer>
  );
}

function AppointmentPackageDrawerContent(props: AppointmentPackageDrawerProps) {
  const { appointmentPackageUuid, duplicate, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const [loading, loadingActions] = useToggle(!!appointmentPackageUuid);
  const [saving, savingActions] = useToggle(false);
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const appointmentPackageQuery = useAppointmentPackageQuery(
    appointmentPackageUuid!,
    {
      enabled: !!appointmentPackageUuid,
      cacheTime: 0,
    },
  );
  const taxesQuery = useTaxesQuery();
  const postAppointmentPackageMutation = useMutation(postAppointmentPackage);
  const patchAppointmentPackageMutation = useMutation(patchAppointmentPackage);

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm<AppointmentPackageForm>({
    mode: "onTouched",
    defaultValues: appointmentPackageUuid
      ? undefined
      : appointmentPackageFormDefault,
  });

  const price = useWatch({
    control,
    name: "packagePrice",
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: AppointmentPackageForm) => {
    savingActions.on();
    try {
      if (appointmentPackageUuid && !duplicate) {
        const payload = convertToAppointmentPatchPackagePayload(values);
        await patchAppointmentPackageMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          appointmentPackageId: appointmentPackageUuid,
          payload: payload,
        });
      } else {
        const payload = convertToAppointmentPostPackagePayload(values);
        await postAppointmentPackageMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          payload,
        });
      }
      toast.success("Saved successfully", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      savingActions.off();
    }
  };

  useEffect(() => {
    if (appointmentPackageQuery.data && taxesQuery.data) {
      const values = convertToAppointmentPackageForm(
        appointmentPackageQuery.data.data,
        taxesQuery.data.data,
        duplicate,
      );
      reset(values);
      loadingActions.off();
    }
  }, [
    loadingActions,
    reset,
    appointmentPackageQuery.data,
    taxesQuery.data,
    duplicate,
  ]);

  const currencySymbol = getCurrencySymbol(app.client!.currencyIso!);

  return (
    <>
      <Panel fullHeight>
        <Panel.Header>
          <PanelHeader title="Appointment package">
            <PanelHeader.ActionMenu>
              <Button
                text="Close"
                buttonType="secondary"
                size="medium"
                onClick={handleClose}
              />
              <Button
                text="Save"
                buttonType="primary"
                size="medium"
                type="button"
                loading={saving}
                onClick={handleSubmit(save)}
              />
            </PanelHeader.ActionMenu>
          </PanelHeader>
        </Panel.Header>
        <Panel.Body overflow="auto">
          {loading ? (
            <Grid container alignItems="center" justifyContent="center">
              <Grid item>
                <Loader className="mt-4" />
              </Grid>
            </Grid>
          ) : (
            <form
              onSubmit={handleSubmit(save)}
              className={styles.form}
              noValidate
            >
              <div className="p-4">
                <TextController
                  autoComplete="off"
                  control={control}
                  label="Name"
                  name="name"
                  placeholder="e.g. Personal Training - 10 credits"
                  required
                />
                <AppointmentTypeSelect
                  label="Type"
                  control={control}
                  name="appointmentType"
                  required
                />
                <NumberController
                  autoComplete="off"
                  control={control}
                  inputWidth="108px"
                  label="Credits"
                  min={1}
                  name="numberOfSessions"
                  required
                />
                <NumberController
                  autoComplete="off"
                  control={control}
                  endOuterText="day(s)"
                  inputWidth="108px"
                  label="Expire credits after"
                  min={1}
                  name="expirationForEachAppointmentCredit"
                  required
                />
                <NumberController
                  className="mb-4"
                  startText={currencySymbol}
                  autoComplete="off"
                  control={control}
                  helperText={`If free, enter ${currencySymbol}0`}
                  label="Package price"
                  min={0}
                  name="packagePrice"
                  required
                />
                {price > 0 && (
                  <>
                    <TaxSelect
                      control={control}
                      hideDropdownIndicator
                      isClearable
                      label="Tax"
                      name="tax"
                      placeholder="-Add-"
                    />
                    <AppointmentDiscountSelect
                      control={control}
                      hideDropdownIndicator
                      isClearable
                      label="Landing page discount"
                      name="discount"
                      placeholder="-Add-"
                    />
                  </>
                )}
                <SwitchController
                  label="Available on mobile app"
                  control={control}
                  name="availableOnMemberApp"
                />
                <Text variant="body-md" color="wolf-600" className="mb-4">
                  Allow clients to purchase this package via the mobile app
                </Text>
                <TextAreaController
                  className="mb-3"
                  autoComplete="off"
                  control={control}
                  label="Short description"
                  maxLength={240}
                  name="description"
                  rows={4}
                />
                <RichController
                  control={control}
                  label="Long description"
                  minHeight="200px"
                  multiLineToolbarPosition="bottom"
                  name="longDescription"
                  valueType="html"
                />
              </div>
            </form>
          )}
        </Panel.Body>
      </Panel>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default AppointmentPackageDrawer;
